import { useEffect, useState } from 'react';
import { adjustScale, changeId, getLayersArray, getRandomLists, getSmartComponentData } from './utils';
import './ai-ppt-template-demo.less';
import { cloneDeep } from 'lodash';
import queryString from 'query-string';
import { traverseTrees } from '@meitu/saas-editor-core/es/utils/tree-kit';
import { ILayerProps, LayerType } from '@meitu/saas-editor-core/es/version-types';
import { packageTemplateConf } from '@/hooks/fetch-template';
import { isPPTDemoAndShowComponentList } from '@/hooks/fetch-template/editor-mode';
import { transferLayersToClientDoText, transferTemplateToClient } from '@/hooks/fetch-template/server-to-client';
import { type IEditor } from '@/instance';
import { fetchAIPPTComponentList, fetchAIPPTMaterialDetailMultiV2, fetchAIPPTTemplateList } from '@/services/ai';
import { adjustImageLayerImage, Layer, PPTTemplate } from '../apply-ppt-template/apply-ppt-template';
import gptContent from './gpt-content';
import RenderStage from './render-stage';

const getNormalComponentData = async (templateList: any[], componentId: string, fonts: any[]) => {
    const randomTemplateList = getRandomLists(templateList, 30);
    const ids = randomTemplateList.map((item) => item.id);
    const details = await fetchAIPPTMaterialDetailMultiV2(ids.join(','));
    const templateDatas = details.data.data;
    const componentDataRsp = await fetchAIPPTMaterialDetailMultiV2(componentId);
    const componentDatas = componentDataRsp.data.data;
    const transferToClient = async (templateData: any) => {
        const data = await transferTemplateToClient(templateData, fonts, '', false);
        return data.template.templateConf;
    };

    const pptContent = await PPTTemplate.getPPTContentDataByComponents(
        gptContent as any,
        transferToClient,
        componentDatas,
    );

    const allStageLayers = [] as ILayerProps[][];
    for (let i = 0; i < templateDatas.length; i++) {
        let templateData = templateDatas[i];

        console.log('templateData===', templateData);
        const clientData = await transferTemplateToClient(JSON.parse(templateData.formula), fonts, '', false, true);

        templateData = clientData.template;

        const templateConf = PPTTemplate.generate({
            pptContents: cloneDeep(pptContent),
            templateData: cloneDeep(templateData),
        });

        if (templateConf.length > 0) {
            allStageLayers.push(changeId(cloneDeep(templateConf[0].layers)));
        }
    }

    return allStageLayers;
};

const AIPPTComponentDemo: React.FC<{ editor: IEditor }> = ({ editor }) => {
    const fonts = editor.services.font.getFonts();

    const [templateLayers, setTemplateLayers] = useState<any[]>([]);
    const [currentTemplates, setCurrentTemplates] = useState<any[]>([]);
    const [scale, setScale] = useState<number>(1);

    useEffect(() => {
        if (fonts.length === 0) {
            return;
        }

        const getInitData = async () => {
            const queryParams: any = queryString.parse(window.location.search);
            const { componentId, smartComponentId } = queryParams;
            const layerAndStageIndexMap: { [layerId: string]: number } = {};

            const rsp = await fetchAIPPTTemplateList();

            const componentListRsp = await fetchAIPPTComponentList();

            const templateList = rsp.data.data.list[0].templates.map((item: any) => {
                return {
                    id: item.template_id,
                };
            });

            const componentList = componentListRsp.data.data.data;
            let allStageLayers = [] as ILayerProps[][];
            if (componentId) {
                allStageLayers = await getNormalComponentData(templateList, componentId, fonts);
            } else if (smartComponentId) {
                allStageLayers = await getSmartComponentData(templateList, componentList, smartComponentId, fonts);
            }

            const allTextLayers = [] as ILayerProps[];
            const allImageLayers = [] as ILayerProps[];
            allStageLayers.forEach((layers) => {
                traverseTrees(layers, (layer: ILayerProps) => {
                    if (layer.layerType === LayerType.TEXT_LAYER) {
                        allTextLayers.push(layer);
                    }
                    if (layer.layerType === LayerType.IMAGE_LAYER) {
                        allImageLayers.push(layer);
                    }
                });
            });

            for (let i = 0; i < allImageLayers.length; i++) {
                const layer = allImageLayers[i];
                if (layer.layerType === LayerType.IMAGE_LAYER) {
                    await adjustImageLayerImage(layer);
                }
            }

            allStageLayers.forEach((layers, index) => {
                traverseTrees(layers, (layer: ILayerProps) => {
                    layerAndStageIndexMap[layer.id] = index;
                    if (layer.layerType === LayerType.TEXT_LAYER) {
                        const { extraInfo } = layer;

                        if (extraInfo?.label) {
                            const { textExpend } = extraInfo.label;
                            if (textExpend) {
                                Layer.adjustTextSize(layer, textExpend);
                            }
                        }
                    }
                });
            });

            await transferLayersToClientDoText(allTextLayers, fonts, layerAndStageIndexMap, false);
            const templateConf = allStageLayers.map((layers) => {
                return {
                    layers: layers,
                };
            });
            templateConf.map((item: any, index) => {
                item.index = index + 1;
            });
            setTemplateLayers(templateConf);
            setCurrentTemplates(templateConf);

            const { stages, extraInfos } = packageTemplateConf(templateConf, {}, true);
            await editor.services.stage.insertWithoutHistory(stages, extraInfos, undefined, true);
        };

        getInitData();
    }, [fonts]);

    useEffect(() => {
        if (!isPPTDemoAndShowComponentList()) {
            return;
        }

        function scaleStage() {
            adjustScale(setScale);
        }

        // 初始化时调整缩放
        setTimeout(() => {
            scaleStage();
        }, 1000);

        // 监听窗口大小变化
        window.addEventListener('resize', scaleStage);

        return () => {
            window.removeEventListener('resize', scaleStage);
        };
    }, [templateLayers]);

    if (!isPPTDemoAndShowComponentList()) {
        return null;
    }

    return (
        <div className="ai-ppt-template-demo-container">
            <div className="ai-ppt-template-demo">
                {currentTemplates.map((item, index) => {
                    return (
                        <div className="demo-item" key={index}>
                            <RenderStage stageIndex={item.index} scale={scale} editor={editor} componentId={''} />
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default AIPPTComponentDemo;
